import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/context/AuthContext';
import { toast } from 'sonner';
import React from 'react';

interface UserPreferences {
  id: string;
  user_id: string;
  selected_academic_year: string | null;
  created_at: string;
  updated_at: string;
}

// Track failed update attempts to prevent infinite loops
let failedUpdateAttempts = 0;
const MAX_FAILED_ATTEMPTS = 3;

export function useUserPreferences() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Reset failed attempts when user changes
  React.useEffect(() => {
    if (!user?.id) {
      failedUpdateAttempts = 0;
    }
  }, [user?.id]);

  // Fetch user preferences
  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['userPreferences', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      // Call the get_or_create function to ensure preferences exist
      const { data, error } = await supabase
        .rpc('get_or_create_user_preferences', { p_user_id: user.id });

      if (error) {
        console.error('Error fetching user preferences:', error);
        throw error;
      }

      return data as UserPreferences;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Update selected academic year with retry logic
  const updateSelectedYear = useMutation({
    mutationFn: async (year: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('user_preferences')
        .update({ selected_academic_year: year })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['userPreferences', user?.id], data);
      // Reset failed attempts on any successful update
      failedUpdateAttempts = 0;
    },
    onError: (error) => {
      console.error('Error updating academic year:', error);
      // Don't show toast here, let the specific mutation call handle it
    },
    retry: 2, // Limit retries to prevent excessive requests
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
  });

  // Safe update function that checks authentication and prevents infinite loops
  const safeUpdateSelectedYear = (year: string) => {
    if (!user?.id) {
      console.warn('Cannot update year preference: user not authenticated');
      return;
    }
    
    // Check if we've had too many failed attempts
    if (failedUpdateAttempts >= MAX_FAILED_ATTEMPTS) {
      console.warn('[UserPreferences] Too many failed update attempts, skipping');
      return;
    }
    
    updateSelectedYear.mutate(year, {
      onError: (error) => {
        failedUpdateAttempts++;
        console.error('Error updating academic year:', error);
        toast.error('Failed to save academic year preference');
      },
      onSuccess: (data) => {
        failedUpdateAttempts = 0;
        queryClient.setQueryData(['userPreferences', user?.id], data);
      }
    });
  };

  return {
    preferences,
    isLoading,
    error,
    selectedAcademicYear: preferences?.selected_academic_year,
    updateSelectedYear: safeUpdateSelectedYear,
    isUpdating: updateSelectedYear.isPending,
    isAuthenticated: !!user?.id,
  };
}

// Hook to get selected year with fallback
export function useSelectedAcademicYear(fallbackYear: string) {
  const { selectedAcademicYear, isLoading } = useUserPreferences();
  
  // Return the user's selected year if available, otherwise use fallback
  return {
    year: selectedAcademicYear || fallbackYear,
    isLoading,
    isUserPreference: !!selectedAcademicYear,
  };
}